import clsx from "clsx";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import classes from "./styles.module.scss";

import type { IImageViewerProps } from ".";

import { Icon } from "@components/atoms/icons";
import { IconButton } from "@components/molecules/iconButton";

// const DOUBLE_CLICK_ZOOM_RATE = 0.5
const ZOOM_RATE = 0.1;
// const DOUBLE_CLICK_MAX = 2

export function ImageViewer({ src, title }: IImageViewerProps) {
  const { t } = useTranslation("common");

  const containerRef = useRef<HTMLDivElement>(null);
  const ref = useRef<HTMLImageElement>(null);
  const [imgScale, setImgScale] = useState(1);
  // const [doubleClickCount, setDoubleClickCount] = useState(0)
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // TODO IMPLEMENT LATER
  /* const handleDoubleClick = (
    e: React.MouseEvent<HTMLImageElement, MouseEvent>
  ) => {
    e.stopPropagation()
    e.preventDefault()

    if (doubleClickCount < DOUBLE_CLICK_MAX) {
      const newScale = imgScale + DOUBLE_CLICK_ZOOM_RATE

      // After zooming, calculate the new scroll position to keep the clicked part visible
      if (containerRef.current && ref.current) {
        const containerRect = containerRef.current.getBoundingClientRect()
        const imageRect = ref.current.getBoundingClientRect()
        const x = (e.clientX - containerRect.left) / containerRect.width
        const y = (e.clientY - containerRect.top) / containerRect.height

        const newScrollLeft =
          x * (imageRect.width * newScale - containerRect.width)
        const newScrollTop =
          y * (imageRect.height * newScale - containerRect.height)

        containerRef.current.scrollLeft = 10
        containerRef.current.scrollTop = newScrollTop

        setImgScale(newScale)
        setDoubleClickCount((prevState) => prevState + 1)
      }
    } else {
      setImgScale(1)
      setDoubleClickCount(0)
    }
  }*/

  const handleMouseDown = (
    event: React.MouseEvent<HTMLImageElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    event.preventDefault();

    setIsDragging(true);
    setDragStart({ x: event.clientX, y: event.clientY });
  };

  const handleMouseMove = (
    event: React.MouseEvent<HTMLImageElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    event.preventDefault();

    if (isDragging && containerRef.current) {
      const deltaX = event.clientX - dragStart.x;
      const deltaY = event.clientY - dragStart.y;

      containerRef.current.scrollLeft -= deltaX;
      containerRef.current.scrollTop -= deltaY;

      setDragStart({ x: event.clientX, y: event.clientY });
    }
  };

  const handleMouseUp = (
    event: React.MouseEvent<HTMLImageElement, MouseEvent>,
  ) => {
    event.stopPropagation();
    event.preventDefault();

    setIsDragging(false);
  };

  return (
    <div className={clsx(classes.imgViewer)}>
      <div className={clsx(classes.container)}>
        <div
          className={clsx(
            classes.imgContainer,
            isDragging && classes.dragCursor,
          )}
          ref={containerRef}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <img
            ref={ref}
            style={{
              width: `${imgScale * 100}%`,
            }}
            src={src}
            alt={title}
            className={clsx(classes.imgContainer__image)}
            // onDoubleClick={handleDoubleClick}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          />
        </div>
      </div>
      <div className={clsx(classes.imgViewer__button)}>
        <IconButton
          icon={<Icon name={"MagniferZoomIn"} type={"linear"} />}
          label={t("zoom-in")}
          onClick={() => setImgScale((prevState) => prevState + ZOOM_RATE)}
          disabled={imgScale > 50}
        />
        <IconButton
          icon={<Icon name={"MagniferZoomOut"} type={"linear"} />}
          label={t("zoom-out")}
          onClick={() => setImgScale((prevState) => prevState - ZOOM_RATE)}
          disabled={imgScale <= 1}
        />
      </div>
    </div>
  );
}
